package dev.lingvo.site.ds

import androidx.compose.runtime.Composable
import com.varabyte.kobweb.compose.dom.svg.Circle
import com.varabyte.kobweb.compose.dom.svg.Line
import com.varabyte.kobweb.compose.dom.svg.Path
import com.varabyte.kobweb.compose.dom.svg.Rect
import com.varabyte.kobweb.compose.dom.svg.SVGFillType
import com.varabyte.kobweb.compose.dom.svg.SVGStrokeLineCap
import com.varabyte.kobweb.compose.dom.svg.SVGStrokeLineJoin
import com.varabyte.kobweb.compose.dom.svg.SVGStrokeType
import com.varabyte.kobweb.compose.dom.svg.Svg
import com.varabyte.kobweb.compose.dom.svg.ViewBox
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.display
import com.varabyte.kobweb.compose.ui.modifiers.height
import com.varabyte.kobweb.compose.ui.modifiers.width
import com.varabyte.kobweb.compose.ui.toAttrs
import com.varabyte.kobweb.silk.style.CssStyle
import com.varabyte.kobweb.silk.style.base
import com.varabyte.kobweb.silk.style.toModifier
import dev.lingvo.shared.ProjectIconKey
import org.jetbrains.compose.web.css.CSSSizeValue
import org.jetbrains.compose.web.css.CSSUnit
import org.jetbrains.compose.web.css.DisplayStyle
import org.jetbrains.compose.web.css.px

val LIconStyle = CssStyle.base {
    Modifier.display(DisplayStyle.Block).width(IconSize.XL).height(IconSize.XL)
}

/**
 * The sizes an icon may be drawn at, and the text each one stands beside.
 *
 * Call sites set `width(n.px).height(n.px)` by hand at five different values — 12, 14, 15, 16 and
 * 18 — against this file's own default of 20. Fifteen was a single site sitting a pixel off its
 * neighbours, which is what an unnamed scale produces: nothing said what the sizes were for, so a
 * new icon took whichever number the nearest example happened to use, and the drift never showed up
 * as a defect because every individual value looked reasonable.
 *
 * Paired with type rather than left abstract, because that is the decision actually being made at
 * the call site: a glyph is nearly always sized against the text it sits next to.
 */
object IconSize {
    /** Beside [TypeScale.Small], where the glyph is a marker rather than a subject. */
    val XS = 12.px

    /** Beside [TypeScale.Small] where the glyph carries meaning of its own. */
    val SM = 14.px

    /** Beside [TypeScale.BodySmall], and inside controls. The common case. */
    val MD = 16.px

    /** Beside [TypeScale.Body]. */
    val LG = 18.px

    /** Standalone, and what [LIconStyle] draws when a call site says nothing. */
    val XL = 20.px

    /**
     * Inside a tinted badge, where the glyph is the subject rather than a label's companion.
     *
     * Added because three surfaces needed exactly this and none of them could say so: the provider
     * grid, the provider detail header and the usage-trend header each drew the same badge inline
     * with a raw `.size()`, and two of the three agreed on 24 while the third had drifted to 28.
     * See [LIconBadge], which is now the only thing that draws it.
     */
    val XXL = 24.px

    /**
     * The one glyph on an otherwise empty screen. [LEmptyState] and nothing else.
     *
     * Deliberately twice [MD] rather than the next step up from [XXL]: this is not a bigger icon
     * beside text, it is the only thing on the page, and a rung that exists for one caller is
     * honest about being a display size rather than pretending to continue the scale.
     */
    val Display = 32.px
}

/**
 * Sizes an icon to one rung of [IconSize], squarely.
 *
 * Two modifiers were needed to set one dimension, so a call site could — and in one case did — set
 * a width and a height that disagreed with each other.
 */
fun Modifier.iconSize(size: CSSSizeValue<CSSUnit.px>): Modifier = width(size).height(size)

/**
 * Pinned Lucide icon data imported from `@lucide/icons` 1.27.0.
 *
 * Keeping the official node type matters: circles and rounded rectangles must remain circles and
 * rounded rectangles instead of becoming approximate paths. See `THIRD_PARTY_NOTICES.md`.
 */
private data class LucideIcon(val name: String, val nodes: List<LucideNode>)

private sealed interface LucideNode {
    data class PathNode(val data: String) : LucideNode
    data class LineNode(
        val startX: Double,
        val startY: Double,
        val endX: Double,
        val endY: Double,
    ) : LucideNode

    data class CircleNode(
        val centerX: Double,
        val centerY: Double,
        val radius: Double,
        val filled: Boolean = false,
    ) : LucideNode

    data class RectNode(
        val x: Double,
        val y: Double,
        val width: Double,
        val height: Double,
        val radiusX: Double,
        val radiusY: Double? = null,
    ) : LucideNode
}

private fun path(data: String): LucideNode = LucideNode.PathNode(data)
private fun line(
    startX: Double,
    startY: Double,
    endX: Double,
    endY: Double,
): LucideNode = LucideNode.LineNode(startX, startY, endX, endY)

private fun circle(
    centerX: Double,
    centerY: Double,
    radius: Double,
    filled: Boolean = false,
): LucideNode = LucideNode.CircleNode(centerX, centerY, radius, filled)

private fun rect(
    x: Double,
    y: Double,
    width: Double,
    height: Double,
    radiusX: Double,
    radiusY: Double? = null,
): LucideNode = LucideNode.RectNode(x, y, width, height, radiusX, radiusY)

@Composable
private fun LIcon(icon: LucideIcon, modifier: Modifier = Modifier) {
    Svg(
        attrs = LIconStyle.toModifier().then(modifier).toAttrs {
            viewBox(ViewBox.sized(24))
            fill(SVGFillType.None)
            stroke(SVGStrokeType.CurrentColor)
            strokeWidth(2)
            strokeLineCap(SVGStrokeLineCap.Round)
            strokeLineJoin(SVGStrokeLineJoin.Round)
            attr("data-lucide", icon.name)
            attr("aria-hidden", "true")
            attr("focusable", "false")
        },
    ) {
        icon.nodes.forEach { node ->
            when (node) {
                is LucideNode.PathNode -> Path { d(node.data) }
                is LucideNode.LineNode -> Line {
                    x1(node.startX)
                    y1(node.startY)
                    x2(node.endX)
                    y2(node.endY)
                }
                is LucideNode.CircleNode -> Circle {
                    cx(node.centerX)
                    cy(node.centerY)
                    r(node.radius)
                    if (node.filled) attr("fill", "currentColor")
                }
                is LucideNode.RectNode -> Rect {
                    x(node.x)
                    y(node.y)
                    width(node.width)
                    height(node.height)
                    rx(node.radiusX)
                    node.radiusY?.let(::ry)
                }
            }
        }
    }
}

private val Check = LucideIcon("check", listOf(path("M20 6 9 17l-5-5")))
private val ChevronDown = LucideIcon("chevron-down", listOf(path("m6 9 6 6 6-6")))
private val ChevronLeft = LucideIcon("chevron-left", listOf(path("m15 18-6-6 6-6")))
private val ChevronRight = LucideIcon("chevron-right", listOf(path("m9 18 6-6-6-6")))
private val Search = LucideIcon("search", listOf(path("m21 21-4.34-4.34"), circle(11.0, 11.0, 8.0)))
private val Plus = LucideIcon("plus", listOf(path("M5 12h14"), path("M12 5v14")))
private val Trash = LucideIcon(
    "trash-2",
    listOf(
        path("M10 11v6"),
        path("M14 11v6"),
        path("M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6"),
        path("M3 6h18"),
        path("M8 6V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"),
    ),
)
private val Settings = LucideIcon(
    "settings",
    listOf(
        path("M9.671 4.136a2.34 2.34 0 0 1 4.659 0 2.34 2.34 0 0 0 3.319 1.915 2.34 2.34 0 0 1 2.33 4.033 2.34 2.34 0 0 0 0 3.831 2.34 2.34 0 0 1-2.33 4.033 2.34 2.34 0 0 0-3.319 1.915 2.34 2.34 0 0 1-4.659 0 2.34 2.34 0 0 0-3.32-1.915 2.34 2.34 0 0 1-2.33-4.033 2.34 2.34 0 0 0 0-3.831A2.34 2.34 0 0 1 6.35 6.051a2.34 2.34 0 0 0 3.319-1.915"),
        circle(12.0, 12.0, 3.0),
    ),
)
private val Wrench = LucideIcon(
    "wrench",
    listOf(path("M14.7 6.3a1 1 0 0 0 0 1.4l1.6 1.6a1 1 0 0 0 1.4 0l3.106-3.105c.32-.322.863-.22.983.218a6 6 0 0 1-8.259 7.057l-7.91 7.91a1 1 0 0 1-2.999-3l7.91-7.91a6 6 0 0 1 7.057-8.259c.438.12.54.662.219.984z")),
)
private val Globe = LucideIcon(
    "globe",
    listOf(circle(12.0, 12.0, 10.0), path("M12 2a14.5 14.5 0 0 0 0 20 14.5 14.5 0 0 0 0-20"), path("M2 12h20")),
)
private val Chart = LucideIcon(
    "chart-no-axes-column-increasing",
    listOf(path("M5 21v-6"), path("M12 21V9"), path("M19 21V3")),
)
private val FileText = LucideIcon(
    "file-text",
    listOf(
        path("M6 22a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h8a2.4 2.4 0 0 1 1.704.706l3.588 3.588A2.4 2.4 0 0 1 20 8v12a2 2 0 0 1-2 2z"),
        path("M14 2v5a1 1 0 0 0 1 1h5"),
        path("M10 9H8"),
        path("M16 13H8"),
        path("M16 17H8"),
    ),
)
private val KeyRound = LucideIcon(
    "key-round",
    listOf(
        path("M2.586 17.414A2 2 0 0 0 2 18.828V21a1 1 0 0 0 1 1h3a1 1 0 0 0 1-1v-1a1 1 0 0 1 1-1h1a1 1 0 0 0 1-1v-1a1 1 0 0 1 1-1h.172a2 2 0 0 0 1.414-.586l.814-.814a6.5 6.5 0 1 0-4-4z"),
        circle(16.5, 7.5, 0.5, filled = true),
    ),
)
private val MessageSquare = LucideIcon(
    "message-square",
    listOf(path("M22 17a2 2 0 0 1-2 2H6.828a2 2 0 0 0-1.414.586l-2.202 2.202A.71.71 0 0 1 2 21.286V5a2 2 0 0 1 2-2h16a2 2 0 0 1 2 2z")),
)
private val History = LucideIcon(
    "rotate-ccw-clock",
    listOf(path("M3 12a9 9 0 1 0 9-9 9.75 9.75 0 0 0-6.74 2.74L3 8"), path("M3 3v5h5"), path("M12 7v5l4 2")),
)
private val Sparkles = LucideIcon(
    "sparkles",
    listOf(
        path("M11.017 2.814a1 1 0 0 1 1.966 0l1.051 5.558a2 2 0 0 0 1.594 1.594l5.558 1.051a1 1 0 0 1 0 1.966l-5.558 1.051a2 2 0 0 0-1.594 1.594l-1.051 5.558a1 1 0 0 1-1.966 0l-1.051-5.558a2 2 0 0 0-1.594-1.594l-5.558-1.051a1 1 0 0 1 0-1.966l5.558-1.051a2 2 0 0 0 1.594-1.594z"),
        path("M20 2v4"),
        path("M22 4h-4"),
        circle(4.0, 20.0, 2.0),
    ),
)
private val Warning = LucideIcon(
    "triangle-alert",
    listOf(path("m21.73 18-8-14a2 2 0 0 0-3.48 0l-8 14A2 2 0 0 0 4 21h16a2 2 0 0 0 1.73-3"), path("M12 9v4"), path("M12 17h.01")),
)
private val Danger = LucideIcon(
    "octagon-alert",
    listOf(
        path("M12 16h.01"),
        path("M12 8v4"),
        path("M15.312 2a2 2 0 0 1 1.414.586l4.688 4.688A2 2 0 0 1 22 8.688v6.624a2 2 0 0 1-.586 1.414l-4.688 4.688a2 2 0 0 1-1.414.586H8.688a2 2 0 0 1-1.414-.586l-4.688-4.688A2 2 0 0 1 2 15.312V8.688a2 2 0 0 1 .586-1.414l4.688-4.688A2 2 0 0 1 8.688 2z"),
    ),
)
private val Info = LucideIcon("info", listOf(circle(12.0, 12.0, 10.0), path("M12 16v-4"), path("M12 8h.01")))
private val Close = LucideIcon("x", listOf(path("M18 6 6 18"), path("m6 6 12 12")))
private val Filter = LucideIcon("list-filter", listOf(path("M2 5h20"), path("M6 12h12"), path("M9 19h6")))
private val Download = LucideIcon("download", listOf(path("M12 15V3"), path("M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"), path("m7 10 5 5 5-5")))
private val Upload = LucideIcon("upload", listOf(path("M12 3v12"), path("m17 8-5-5-5 5"), path("M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4")))
private val Copy = LucideIcon("copy", listOf(rect(8.0, 8.0, 14.0, 14.0, 2.0, 2.0), path("M4 16c-1.1 0-2-.9-2-2V4c0-1.1.9-2 2-2h10c1.1 0 2 .9 2 2")))
private val ExternalLink = LucideIcon("external-link", listOf(path("M15 3h6v6"), path("M10 14 21 3"), path("M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6")))
private val User = LucideIcon("user-round", listOf(circle(12.0, 8.0, 5.0), path("M20 21a8 8 0 0 0-16 0")))
private val Logout = LucideIcon("log-out", listOf(path("m16 17 5-5-5-5"), path("M21 12H9"), path("M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4")))
private val Sun = LucideIcon("sun", listOf(circle(12.0, 12.0, 4.0), path("M12 2v2"), path("M12 20v2"), path("m4.93 4.93 1.41 1.41"), path("m17.66 17.66 1.41 1.41"), path("M2 12h2"), path("M20 12h2"), path("m6.34 17.66-1.41 1.41"), path("m19.07 4.93-1.41 1.41")))
private val Moon = LucideIcon("moon", listOf(path("M20.985 12.486a9 9 0 1 1-9.473-9.472c.405-.022.617.46.402.803a6 6 0 0 0 8.268 8.268c.344-.215.825-.004.803.401")))
private val Menu = LucideIcon("menu", listOf(path("M4 5h16"), path("M4 12h16"), path("M4 19h16")))
private val Mail = LucideIcon("mail", listOf(path("m22 7-8.991 5.727a2 2 0 0 1-2.009 0L2 7"), rect(2.0, 4.0, 20.0, 16.0, 2.0)))
private val Grid = LucideIcon("layout-grid", listOf(rect(3.0, 3.0, 7.0, 7.0, 1.0), rect(14.0, 3.0, 7.0, 7.0, 1.0), rect(14.0, 14.0, 7.0, 7.0, 1.0), rect(3.0, 14.0, 7.0, 7.0, 1.0)))
private val Help = LucideIcon("circle-question-mark", listOf(circle(12.0, 12.0, 10.0), path("M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3"), path("M12 17h.01")))
private val Languages = LucideIcon(
    "languages",
    listOf(
        path("m5 8 6 6"),
        path("m4 14 6-6 2-3"),
        path("M2 5h12"),
        path("M7 2h1"),
        path("m22 22-5-10-5 10"),
        path("M14 18h6"),
    ),
)
private val CircleCheck = LucideIcon(
    "circle-check-big",
    listOf(path("M21.801 10A10 10 0 1 1 17 3.335"), path("m9 11 3 3L22 4")),
)
private val Refresh = LucideIcon(
    "refresh-cw",
    listOf(
        path("M3 12a9 9 0 0 1 9-9 9.75 9.75 0 0 1 6.74 2.74L21 8"),
        path("M21 3v5h-5"),
        path("M21 12a9 9 0 0 1-9 9 9.75 9.75 0 0 1-6.74-2.74L3 16"),
        path("M8 16H3v5"),
    ),
)
private val Braces = LucideIcon(
    "braces",
    listOf(
        path("M8 3H7a2 2 0 0 0-2 2v5a2 2 0 0 1-2 2 2 2 0 0 1 2 2v5c0 1.1.9 2 2 2h1"),
        path("M16 21h1a2 2 0 0 0 2-2v-5c0-1.1.9-2 2-2a2 2 0 0 1-2-2V5a2 2 0 0 0-2-2h-1"),
    ),
)
private val Files = LucideIcon(
    "files",
    listOf(
        path("M15 2h-4a2 2 0 0 0-2 2v11a2 2 0 0 0 2 2h8a2 2 0 0 0 2-2V8"),
        path("M16.706 2.706A2.4 2.4 0 0 0 15 2v5a1 1 0 0 0 1 1h5a2.4 2.4 0 0 0-.706-1.706z"),
        path("M5 7a2 2 0 0 0-2 2v11a2 2 0 0 0 2 2h8a2 2 0 0 0 1.732-1"),
    ),
)
private val SearchX = LucideIcon(
    "search-x",
    listOf(
        path("m13.5 8.5-5 5"),
        path("m8.5 8.5 5 5"),
        circle(11.0, 11.0, 8.0),
        path("m21 21-4.3-4.3"),
    ),
)
private val Bot = LucideIcon(
    "bot",
    listOf(
        path("M12 8V4H8"),
        rect(4.0, 8.0, 16.0, 12.0, 2.0),
        path("M2 14h2"),
        path("M20 14h2"),
        path("M15 13v2"),
        path("M9 13v2"),
    ),
)
private val ArrowRight = LucideIcon(
    "arrow-right",
    listOf(path("M5 12h14"), path("m12 5 7 7-7 7")),
)
private val CalendarDays = LucideIcon(
    "calendar-days",
    listOf(
        path("M8 2v4"),
        path("M16 2v4"),
        rect(3.0, 4.0, 18.0, 18.0, 2.0),
        path("M3 10h18"),
        path("M8 14h.01"),
        path("M12 14h.01"),
        path("M16 14h.01"),
        path("M8 18h.01"),
        path("M12 18h.01"),
        path("M16 18h.01"),
    ),
)
private val Folder = LucideIcon(
    "folder",
    listOf(
        path("M20 20a2 2 0 0 0 2-2V8a2 2 0 0 0-2-2h-7.9a2 2 0 0 1-1.69-.9L9.6 3.9A2 2 0 0 0 7.93 3H4a2 2 0 0 0-2 2v13a2 2 0 0 0 2 2Z"),
    ),
)
private val Earth = LucideIcon(
    "earth",
    listOf(
        path("M21.54 15H17a2 2 0 0 0-2 2v4.54"),
        path("M7 3.34V5a3 3 0 0 0 3 3a2 2 0 0 1 2 2c0 1.1.9 2 2 2a2 2 0 0 0 2-2c0-1.1.9-2 2-2h3.17"),
        path("M11 21.95V18a2 2 0 0 0-2-2a2 2 0 0 1-2-2v-1a2 2 0 0 0-2-2H2.05"),
        circle(12.0, 12.0, 10.0),
    ),
)
private val Smartphone = LucideIcon(
    "smartphone",
    listOf(
        rect(5.0, 2.0, 14.0, 20.0, 2.0, 2.0),
        path("M12 18h.01"),
    ),
)
private val AppWindow = LucideIcon(
    "app-window",
    listOf(
        rect(2.0, 4.0, 20.0, 16.0, 2.0),
        path("M10 4v4"),
        path("M2 8h20"),
        path("M6 4v4"),
    ),
)
private val Box = LucideIcon(
    "box",
    listOf(
        path("M21 8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16Z"),
        path("m3.3 7 8.7 5 8.7-5"),
        path("M12 22V12"),
    ),
)
private val BookOpen = LucideIcon(
    "book-open",
    listOf(
        path("M12 5v16"),
        path("M20.001 19A2 2 0 0022 17V5a2 2 0 00-1.999-2L16 3.002A5 5 0 0012 5a5 5 0 00-4-2H4a2 2 0 00-2 2v12a2 2 0 001.999 2H8a5 5 0 014 2 5 5 0 014-2z"),
    ),
)
private val Gamepad2 = LucideIcon(
    "gamepad-2",
    listOf(
        line(6.0, 11.0, 10.0, 11.0),
        line(8.0, 9.0, 8.0, 13.0),
        line(15.0, 12.0, 15.01, 12.0),
        line(18.0, 10.0, 18.01, 10.0),
        path("M17.32 5H6.68a4 4 0 0 0-3.978 3.59c-.006.052-.01.101-.017.152C2.604 9.416 2 14.456 2 16a3 3 0 0 0 3 3c1 0 1.5-.5 2-1l1.414-1.414A2 2 0 0 1 9.828 16h4.344a2 2 0 0 1 1.414.586L17 18c.5.5 1 1 2 1a3 3 0 0 0 3-3c0-1.545-.604-6.584-.685-7.258-.007-.05-.011-.1-.017-.151A4 4 0 0 0 17.32 5z"),
    ),
)
private val Eye = LucideIcon(
    "eye",
    listOf(
        path("M2.062 12.348a1 1 0 0 1 0-.696 10.75 10.75 0 0 1 19.876 0 1 1 0 0 1 0 .696 10.75 10.75 0 0 1-19.876 0"),
        circle(12.0, 12.0, 3.0),
    ),
)
private val EyeOff = LucideIcon(
    "eye-off",
    listOf(
        path("M10.733 5.076a10.744 10.744 0 0 1 11.205 6.575 1 1 0 0 1 0 .696 10.747 10.747 0 0 1-1.444 2.49"),
        path("M14.084 14.158a3 3 0 0 1-4.242-4.242"),
        path("M17.479 17.499a10.75 10.75 0 0 1-15.417-5.151 1 1 0 0 1 0-.696 10.75 10.75 0 0 1 4.446-5.143"),
        path("m2 2 20 20"),
    ),
)

private val Play = LucideIcon(
    "play",
    listOf(
        path("M5 5a2 2 0 0 1 3.008-1.728l11.997 6.998a2 2 0 0 1 .003 3.458l-12 7A2 2 0 0 1 5 19z"),
    ),
)
private val WandSparkles = LucideIcon(
    "wand-sparkles",
    listOf(
        path("m21.64 3.64-1.28-1.28a1.21 1.21 0 0 0-1.72 0L2.36 18.64a1.21 1.21 0 0 0 0 1.72l1.28 1.28a1.2 1.2 0 0 0 1.72 0L21.64 5.36a1.2 1.2 0 0 0 0-1.72"),
        path("m14 7 3 3"),
        path("M5 6v4"),
        path("M19 14v4"),
        path("M10 2v2"),
        path("M7 8H3"),
        path("M21 16h-4"),
        path("M11 3H9"),
    ),
)
private val CheckCheck = LucideIcon("check-check", listOf(path("M18 6 7 17l-5-5"), path("m22 10-7.5 7.5L13 16")))
private val Zap = LucideIcon(
    "zap",
    listOf(
        path("M15.914 4a1.5 1.5 0 00-2.474-1.561l-9 9A1.5 1.5 0 005.5 14h4.002a.5.5 0 01.471.666L8.086 20a1.5 1.5 0 002.475 1.56l9-9A1.5 1.5 0 0018.5 10h-3.997a.5.5 0 01-.472-.667z"),
    ),
)
private val Undo2 = LucideIcon(
    "undo-2",
    listOf(
        path("M9 14 4 9l5-5"),
        path("M4 9h10.5a5.5 5.5 0 0 1 5.5 5.5a5.5 5.5 0 0 1-5.5 5.5H11"),
    ),
)
private val Ellipsis = LucideIcon(
    "ellipsis",
    listOf(
        circle(12.0, 12.0, 1.0),
        circle(19.0, 12.0, 1.0),
        circle(5.0, 12.0, 1.0),
    ),
)
private val ChevronUp = LucideIcon("chevron-up", listOf(path("m18 15-6-6-6 6")))
private val ArrowLeft = LucideIcon("arrow-left", listOf(path("m12 19-7-7 7-7"), path("M19 12H5")))
private val ArrowUp = LucideIcon("arrow-up", listOf(path("m5 12 7-7 7 7"), path("M12 19V5")))
private val ArrowDown = LucideIcon("arrow-down", listOf(path("M12 5v14"), path("m19 12-7 7-7-7")))
private val ArrowUpDown = LucideIcon(
    "arrow-up-down",
    listOf(
        path("m21 16-4 4-4-4"),
        path("M17 20V4"),
        path("m3 8 4-4 4 4"),
        path("M7 4v16"),
    ),
)
private val CircleAlert = LucideIcon(
    "circle-alert",
    listOf(
        circle(12.0, 12.0, 10.0),
        line(12.0, 8.0, 12.0, 12.0),
        line(12.0, 16.0, 12.01, 16.0),
    ),
)
private val CircleX = LucideIcon(
    "circle-x",
    listOf(
        circle(12.0, 12.0, 10.0),
        path("m15 9-6 6"),
        path("m9 9 6 6"),
    ),
)
private val Clock = LucideIcon("clock", listOf(circle(12.0, 12.0, 10.0), path("M12 6v6l4 2")))
private val ListChecks = LucideIcon(
    "list-checks",
    listOf(
        path("M13 5h8"),
        path("M13 12h8"),
        path("M13 19h8"),
        path("m3 17 2 2 4-4"),
        path("m3 7 2 2 4-4"),
    ),
)
private val Pause = LucideIcon("pause", listOf(rect(14.0, 3.0, 5.0, 18.0, 1.0), rect(5.0, 3.0, 5.0, 18.0, 1.0)))
private val TrendingUp = LucideIcon("trending-up", listOf(path("M16 7h6v6"), path("m22 7-8.5 8.5-5-5L2 17")))
private val TrendingDown = LucideIcon("trending-down", listOf(path("M16 17h6v-6"), path("m22 17-8.5-8.5-5 5L2 7")))
private val Keyboard = LucideIcon(
    "keyboard",
    listOf(
        path("M10 8h.01"),
        path("M12 12h.01"),
        path("M14 8h.01"),
        path("M16 12h.01"),
        path("M18 8h.01"),
        path("M6 8h.01"),
        path("M7 16h10"),
        path("M8 12h.01"),
        rect(2.0, 4.0, 20.0, 16.0, 2.0),
    ),
)
private val PanelLeft = LucideIcon("panel-left", listOf(rect(3.0, 3.0, 18.0, 18.0, 2.0), path("M9 3v18")))
private val Lock = LucideIcon("lock", listOf(rect(3.0, 11.0, 18.0, 11.0, 2.0, 2.0), path("M7 11V7a5 5 0 0 1 10 0v4")))
private val SquareCheckBig = LucideIcon(
    "square-check-big",
    listOf(
        path("M21 10.656V19a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h12.344"),
        path("m9 11 3 3L22 4"),
    ),
)
private val SquarePen = LucideIcon(
    "square-pen",
    listOf(
        path("M12 3H5a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"),
        path("M18.375 2.625a1 1 0 0 1 3 3l-9.013 9.014a2 2 0 0 1-.853.505l-2.873.84a.5.5 0 0 1-.62-.62l.84-2.873a2 2 0 0 1 .506-.852z"),
    ),
)
private val GitBranch = LucideIcon(
    "git-branch",
    listOf(
        path("M15 6a9 9 0 0 0-9 9V3"),
        circle(18.0, 6.0, 3.0),
        circle(6.0, 18.0, 3.0),
    ),
)
private val Bell = LucideIcon(
    "bell",
    listOf(
        path("M10.268 21a2 2 0 0 0 3.464 0"),
        path("M3.262 15.326A1 1 0 0 0 4 17h16a1 1 0 0 0 .74-1.673C19.41 13.956 18 12.499 18 8A6 6 0 0 0 6 8c0 4.499-1.411 5.956-2.738 7.326"),
    ),
)
private val Shield = LucideIcon(
    "shield",
    listOf(
        path("M20 13c0 5-3.5 7.5-7.66 8.95a1 1 0 0 1-.67-.01C7.5 20.5 4 18 4 13V6a1 1 0 0 1 1-1c2 0 4.5-1.2 6.24-2.72a1.17 1.17 0 0 1 1.52 0C14.51 3.81 17 5 19 5a1 1 0 0 1 1 1z"),
    ),
)
private val CreditCard = LucideIcon(
    "credit-card",
    listOf(
        rect(2.0, 5.0, 20.0, 14.0, 2.0),
        line(2.0, 10.0, 22.0, 10.0),
    ),
)
private val Receipt = LucideIcon(
    "receipt",
    listOf(
        path("M12 17V7"),
        path("M16 8h-6a2 2 0 0 0 0 4h4a2 2 0 0 1 0 4H8"),
        path("M4 3a1 1 0 0 1 1-1 1.3 1.3 0 0 1 .7.2l.933.6a1.3 1.3 0 0 0 1.4 0l.934-.6a1.3 1.3 0 0 1 1.4 0l.933.6a1.3 1.3 0 0 0 1.4 0l.933-.6a1.3 1.3 0 0 1 1.4 0l.934.6a1.3 1.3 0 0 0 1.4 0l.933-.6A1.3 1.3 0 0 1 19 2a1 1 0 0 1 1 1v18a1 1 0 0 1-1 1 1.3 1.3 0 0 1-.7-.2l-.933-.6a1.3 1.3 0 0 0-1.4 0l-.934.6a1.3 1.3 0 0 1-1.4 0l-.933-.6a1.3 1.3 0 0 0-1.4 0l-.933.6a1.3 1.3 0 0 1-1.4 0l-.934-.6a1.3 1.3 0 0 0-1.4 0l-.933.6a1.3 1.3 0 0 1-.7.2 1 1 0 0 1-1-1z"),
    ),
)
private val Webhook = LucideIcon(
    "webhook",
    listOf(
        path("M18 16.98h-5.99c-1.1 0-1.95.94-2.48 1.9A4 4 0 0 1 2 17c.01-.7.2-1.4.57-2"),
        path("m6 17 3.13-5.78c.53-.97.1-2.18-.5-3.1a4 4 0 1 1 6.89-4.06"),
        path("m12 6 3.13 5.73C15.66 12.7 16.9 13 18 13a4 4 0 0 1 0 8"),
    ),
)
private val Users = LucideIcon(
    "users",
    listOf(
        path("M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2"),
        path("M16 3.128a4 4 0 0 1 0 7.744"),
        path("M22 21v-2a4 4 0 0 0-3-3.87"),
        circle(9.0, 7.0, 4.0),
    ),
)
private val UserPlus = LucideIcon(
    "user-plus",
    listOf(
        path("M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2"),
        circle(9.0, 7.0, 4.0),
        line(19.0, 8.0, 19.0, 14.0),
        line(22.0, 11.0, 16.0, 11.0),
    ),
)
private val ScrollText = LucideIcon(
    "scroll-text",
    listOf(
        path("M15 12h-5"),
        path("M15 8h-5"),
        path("M19 17V5a2 2 0 0 0-2-2H4"),
        path("M8 21h12a2 2 0 0 0 2-2v-1a1 1 0 0 0-1-1H11a1 1 0 0 0-1 1v1a2 2 0 1 1-4 0V5a2 2 0 1 0-4 0v2a1 1 0 0 0 1 1h3"),
    ),
)
private val Coins = LucideIcon(
    "coins",
    listOf(
        path("M13.744 17.736a6 6 0 1 1-7.48-7.48"),
        path("M15 6h1v4"),
        path("m6.134 14.768.866-.5 2 3.464"),
        circle(16.0, 8.0, 6.0),
    ),
)
private val ChartLine = LucideIcon("chart-line", listOf(path("M3 3v16a2 2 0 0 0 2 2h16"), path("m19 9-5 5-4-4-3 3")))
private val Tag = LucideIcon(
    "tag",
    listOf(
        path("M12.586 2.586A2 2 0 0 0 11.172 2H4a2 2 0 0 0-2 2v7.172a2 2 0 0 0 .586 1.414l8.704 8.704a2.426 2.426 0 0 0 3.42 0l6.58-6.58a2.426 2.426 0 0 0 0-3.42z"),
        circle(7.5, 7.5, 0.5, filled = true),
    ),
)
private val Tags = LucideIcon(
    "tags",
    listOf(
        path("M13.172 2a2 2 0 0 1 1.414.586l6.71 6.71a2.4 2.4 0 0 1 0 3.408l-4.592 4.592a2.4 2.4 0 0 1-3.408 0l-6.71-6.71A2 2 0 0 1 6 9.172V3a1 1 0 0 1 1-1z"),
        path("M2 7v6.172a2 2 0 0 0 .586 1.414l6.71 6.71a2.4 2.4 0 0 0 3.191.193"),
        circle(10.5, 6.5, 0.5, filled = true),
    ),
)
private val ArchiveRestore = LucideIcon(
    "archive-restore",
    listOf(
        rect(2.0, 3.0, 20.0, 5.0, 1.0),
        path("M4 8v11a2 2 0 0 0 2 2h2"),
        path("M20 8v11a2 2 0 0 1-2 2h-2"),
        path("m9 15 3-3 3 3"),
        path("M12 12v9"),
    ),
)

@Composable fun LCheckIcon(modifier: Modifier = Modifier) = LIcon(Check, modifier)
@Composable fun LChevronDownIcon(modifier: Modifier = Modifier) = LIcon(ChevronDown, modifier)
@Composable fun LChevronLeftIcon(modifier: Modifier = Modifier) = LIcon(ChevronLeft, modifier)
@Composable fun LChevronRightIcon(modifier: Modifier = Modifier) = LIcon(ChevronRight, modifier)
@Composable fun LSearchIcon(modifier: Modifier = Modifier) = LIcon(Search, modifier)
@Composable fun LPlusIcon(modifier: Modifier = Modifier) = LIcon(Plus, modifier)
@Composable fun LTrashIcon(modifier: Modifier = Modifier) = LIcon(Trash, modifier)
@Composable fun LSettingsIcon(modifier: Modifier = Modifier) = LIcon(Settings, modifier)
@Composable fun LWrenchIcon(modifier: Modifier = Modifier) = LIcon(Wrench, modifier)
@Composable fun LGlobeIcon(modifier: Modifier = Modifier) = LIcon(Globe, modifier)
@Composable fun LChartIcon(modifier: Modifier = Modifier) = LIcon(Chart, modifier)
@Composable fun LFileIcon(modifier: Modifier = Modifier) = LIcon(FileText, modifier)
@Composable fun LKeyIcon(modifier: Modifier = Modifier) = LIcon(KeyRound, modifier)
@Composable fun LCommentIcon(modifier: Modifier = Modifier) = LIcon(MessageSquare, modifier)
@Composable fun LHistoryIcon(modifier: Modifier = Modifier) = LIcon(History, modifier)
@Composable fun LSparkleIcon(modifier: Modifier = Modifier) = LIcon(Sparkles, modifier)
@Composable fun LWarningIcon(modifier: Modifier = Modifier) = LIcon(Warning, modifier)
@Composable fun LDangerIcon(modifier: Modifier = Modifier) = LIcon(Danger, modifier)
@Composable fun LInfoIcon(modifier: Modifier = Modifier) = LIcon(Info, modifier)
@Composable fun LCloseIcon(modifier: Modifier = Modifier) = LIcon(Close, modifier)
@Composable fun LFilterIcon(modifier: Modifier = Modifier) = LIcon(Filter, modifier)
@Composable fun LDownloadIcon(modifier: Modifier = Modifier) = LIcon(Download, modifier)
@Composable fun LUploadIcon(modifier: Modifier = Modifier) = LIcon(Upload, modifier)
@Composable fun LCopyIcon(modifier: Modifier = Modifier) = LIcon(Copy, modifier)
@Composable fun LExternalLinkIcon(modifier: Modifier = Modifier) = LIcon(ExternalLink, modifier)
@Composable fun LUserIcon(modifier: Modifier = Modifier) = LIcon(User, modifier)
@Composable fun LLogoutIcon(modifier: Modifier = Modifier) = LIcon(Logout, modifier)
@Composable fun LSunIcon(modifier: Modifier = Modifier) = LIcon(Sun, modifier)
@Composable fun LMoonIcon(modifier: Modifier = Modifier) = LIcon(Moon, modifier)
@Composable fun LMenuIcon(modifier: Modifier = Modifier) = LIcon(Menu, modifier)
@Composable fun LMailIcon(modifier: Modifier = Modifier) = LIcon(Mail, modifier)
@Composable fun LGridIcon(modifier: Modifier = Modifier) = LIcon(Grid, modifier)
@Composable fun LHelpIcon(modifier: Modifier = Modifier) = LIcon(Help, modifier)
@Composable fun LLanguagesIcon(modifier: Modifier = Modifier) = LIcon(Languages, modifier)
@Composable fun LCircleCheckIcon(modifier: Modifier = Modifier) = LIcon(CircleCheck, modifier)
@Composable fun LRefreshIcon(modifier: Modifier = Modifier) = LIcon(Refresh, modifier)
@Composable fun LBracesIcon(modifier: Modifier = Modifier) = LIcon(Braces, modifier)
@Composable fun LFilesIcon(modifier: Modifier = Modifier) = LIcon(Files, modifier)
@Composable fun LSearchXIcon(modifier: Modifier = Modifier) = LIcon(SearchX, modifier)
@Composable fun LBotIcon(modifier: Modifier = Modifier) = LIcon(Bot, modifier)
@Composable fun LArrowRightIcon(modifier: Modifier = Modifier) = LIcon(ArrowRight, modifier)
@Composable fun LCalendarIcon(modifier: Modifier = Modifier) = LIcon(CalendarDays, modifier)
@Composable fun LFolderIcon(modifier: Modifier = Modifier) = LIcon(Folder, modifier)
@Composable fun LEarthIcon(modifier: Modifier = Modifier) = LIcon(Earth, modifier)
@Composable fun LSmartphoneIcon(modifier: Modifier = Modifier) = LIcon(Smartphone, modifier)
@Composable fun LAppWindowIcon(modifier: Modifier = Modifier) = LIcon(AppWindow, modifier)
@Composable fun LBoxIcon(modifier: Modifier = Modifier) = LIcon(Box, modifier)
@Composable fun LBookOpenIcon(modifier: Modifier = Modifier) = LIcon(BookOpen, modifier)
@Composable fun LGamepadIcon(modifier: Modifier = Modifier) = LIcon(Gamepad2, modifier)
@Composable fun LEyeIcon(modifier: Modifier = Modifier) = LIcon(Eye, modifier)
@Composable fun LEyeOffIcon(modifier: Modifier = Modifier) = LIcon(EyeOff, modifier)
@Composable fun LPlayIcon(modifier: Modifier = Modifier) = LIcon(Play, modifier)
@Composable fun LWandSparklesIcon(modifier: Modifier = Modifier) = LIcon(WandSparkles, modifier)
@Composable fun LCheckCheckIcon(modifier: Modifier = Modifier) = LIcon(CheckCheck, modifier)
@Composable fun LZapIcon(modifier: Modifier = Modifier) = LIcon(Zap, modifier)
@Composable fun LUndoIcon(modifier: Modifier = Modifier) = LIcon(Undo2, modifier)
@Composable fun LEllipsisIcon(modifier: Modifier = Modifier) = LIcon(Ellipsis, modifier)
@Composable fun LChevronUpIcon(modifier: Modifier = Modifier) = LIcon(ChevronUp, modifier)
@Composable fun LArrowLeftIcon(modifier: Modifier = Modifier) = LIcon(ArrowLeft, modifier)
@Composable fun LArrowUpIcon(modifier: Modifier = Modifier) = LIcon(ArrowUp, modifier)
@Composable fun LArrowDownIcon(modifier: Modifier = Modifier) = LIcon(ArrowDown, modifier)
@Composable fun LArrowUpDownIcon(modifier: Modifier = Modifier) = LIcon(ArrowUpDown, modifier)
@Composable fun LCircleAlertIcon(modifier: Modifier = Modifier) = LIcon(CircleAlert, modifier)
@Composable fun LCircleXIcon(modifier: Modifier = Modifier) = LIcon(CircleX, modifier)
@Composable fun LClockIcon(modifier: Modifier = Modifier) = LIcon(Clock, modifier)
@Composable fun LListChecksIcon(modifier: Modifier = Modifier) = LIcon(ListChecks, modifier)
@Composable fun LPauseIcon(modifier: Modifier = Modifier) = LIcon(Pause, modifier)
@Composable fun LTrendingUpIcon(modifier: Modifier = Modifier) = LIcon(TrendingUp, modifier)
@Composable fun LTrendingDownIcon(modifier: Modifier = Modifier) = LIcon(TrendingDown, modifier)
@Composable fun LKeyboardIcon(modifier: Modifier = Modifier) = LIcon(Keyboard, modifier)
@Composable fun LPanelLeftIcon(modifier: Modifier = Modifier) = LIcon(PanelLeft, modifier)
@Composable fun LLockIcon(modifier: Modifier = Modifier) = LIcon(Lock, modifier)
@Composable fun LSquareCheckIcon(modifier: Modifier = Modifier) = LIcon(SquareCheckBig, modifier)
@Composable fun LSquarePenIcon(modifier: Modifier = Modifier) = LIcon(SquarePen, modifier)
@Composable fun LGitBranchIcon(modifier: Modifier = Modifier) = LIcon(GitBranch, modifier)
@Composable fun LBellIcon(modifier: Modifier = Modifier) = LIcon(Bell, modifier)
@Composable fun LShieldIcon(modifier: Modifier = Modifier) = LIcon(Shield, modifier)
@Composable fun LCreditCardIcon(modifier: Modifier = Modifier) = LIcon(CreditCard, modifier)
@Composable fun LReceiptIcon(modifier: Modifier = Modifier) = LIcon(Receipt, modifier)
@Composable fun LWebhookIcon(modifier: Modifier = Modifier) = LIcon(Webhook, modifier)
@Composable fun LUsersIcon(modifier: Modifier = Modifier) = LIcon(Users, modifier)
@Composable fun LUserPlusIcon(modifier: Modifier = Modifier) = LIcon(UserPlus, modifier)
@Composable fun LScrollTextIcon(modifier: Modifier = Modifier) = LIcon(ScrollText, modifier)
@Composable fun LCoinsIcon(modifier: Modifier = Modifier) = LIcon(Coins, modifier)
@Composable fun LChartLineIcon(modifier: Modifier = Modifier) = LIcon(ChartLine, modifier)
@Composable fun LTagIcon(modifier: Modifier = Modifier) = LIcon(Tag, modifier)
@Composable fun LTagsIcon(modifier: Modifier = Modifier) = LIcon(Tags, modifier)
@Composable fun LArchiveRestoreIcon(modifier: Modifier = Modifier) = LIcon(ArchiveRestore, modifier)

/** The stable shared project-icon enum rendered through the pinned Lucide node data above. */
@Composable
internal fun LProjectIdentityIcon(
    iconKey: ProjectIconKey,
    modifier: Modifier = Modifier,
) {
    when (iconKey) {
        ProjectIconKey.MONOGRAM -> LFolderIcon(modifier)
        ProjectIconKey.BRACES -> LBracesIcon(modifier)
        ProjectIconKey.GLOBE_2 -> LEarthIcon(modifier)
        ProjectIconKey.SMARTPHONE -> LSmartphoneIcon(modifier)
        ProjectIconKey.APP_WINDOW -> LAppWindowIcon(modifier)
        ProjectIconKey.BOX -> LBoxIcon(modifier)
        ProjectIconKey.BOOK_OPEN -> LBookOpenIcon(modifier)
        ProjectIconKey.GAMEPAD_2 -> LGamepadIcon(modifier)
    }
}

/**
 * Every glyph this file draws, as `data-lucide` name to renderer.
 *
 * A registry rather than reflection: Kotlin/JS has none worth relying on, and a wrapper that is
 * never added here is a wrapper no contract test ever sees. The key is the upstream Lucide name the
 * wrapper renders into `data-lucide`, so a test can assert the rendered attribute against the key
 * instead of trusting the pair.
 *
 * 88 entries — one per public wrapper below. [LProjectIdentityIcon] is deliberately absent: it
 * re-draws glyphs already listed here rather than adding one.
 *
 * Public rather than `internal`, because `internal` is Gradle-module-scoped and both consumers are
 * in `:site`: the `/ds` gallery's icon sheet and `LucideIconContractTest`. A registry that the
 * contract test cannot see is a registry that guarantees nothing.
 */
val ALL_ICONS: List<Pair<String, @Composable (Modifier) -> Unit>> = listOf(
    icon("check") { LCheckIcon(it) },
    icon("chevron-down") { LChevronDownIcon(it) },
    icon("chevron-left") { LChevronLeftIcon(it) },
    icon("chevron-right") { LChevronRightIcon(it) },
    icon("search") { LSearchIcon(it) },
    icon("plus") { LPlusIcon(it) },
    icon("trash-2") { LTrashIcon(it) },
    icon("settings") { LSettingsIcon(it) },
    icon("wrench") { LWrenchIcon(it) },
    icon("globe") { LGlobeIcon(it) },
    icon("chart-no-axes-column-increasing") { LChartIcon(it) },
    icon("file-text") { LFileIcon(it) },
    icon("key-round") { LKeyIcon(it) },
    icon("message-square") { LCommentIcon(it) },
    icon("rotate-ccw-clock") { LHistoryIcon(it) },
    icon("sparkles") { LSparkleIcon(it) },
    icon("triangle-alert") { LWarningIcon(it) },
    icon("octagon-alert") { LDangerIcon(it) },
    icon("info") { LInfoIcon(it) },
    icon("x") { LCloseIcon(it) },
    icon("list-filter") { LFilterIcon(it) },
    icon("download") { LDownloadIcon(it) },
    icon("upload") { LUploadIcon(it) },
    icon("copy") { LCopyIcon(it) },
    icon("external-link") { LExternalLinkIcon(it) },
    icon("user-round") { LUserIcon(it) },
    icon("log-out") { LLogoutIcon(it) },
    icon("sun") { LSunIcon(it) },
    icon("moon") { LMoonIcon(it) },
    icon("menu") { LMenuIcon(it) },
    icon("mail") { LMailIcon(it) },
    icon("layout-grid") { LGridIcon(it) },
    icon("circle-question-mark") { LHelpIcon(it) },
    icon("languages") { LLanguagesIcon(it) },
    icon("circle-check-big") { LCircleCheckIcon(it) },
    icon("refresh-cw") { LRefreshIcon(it) },
    icon("braces") { LBracesIcon(it) },
    icon("files") { LFilesIcon(it) },
    icon("search-x") { LSearchXIcon(it) },
    icon("bot") { LBotIcon(it) },
    icon("arrow-right") { LArrowRightIcon(it) },
    icon("calendar-days") { LCalendarIcon(it) },
    icon("folder") { LFolderIcon(it) },
    icon("earth") { LEarthIcon(it) },
    icon("smartphone") { LSmartphoneIcon(it) },
    icon("app-window") { LAppWindowIcon(it) },
    icon("box") { LBoxIcon(it) },
    icon("book-open") { LBookOpenIcon(it) },
    icon("gamepad-2") { LGamepadIcon(it) },
    icon("eye") { LEyeIcon(it) },
    icon("eye-off") { LEyeOffIcon(it) },
    icon("play") { LPlayIcon(it) },
    icon("wand-sparkles") { LWandSparklesIcon(it) },
    icon("check-check") { LCheckCheckIcon(it) },
    icon("zap") { LZapIcon(it) },
    icon("undo-2") { LUndoIcon(it) },
    icon("ellipsis") { LEllipsisIcon(it) },
    icon("chevron-up") { LChevronUpIcon(it) },
    icon("arrow-left") { LArrowLeftIcon(it) },
    icon("arrow-up") { LArrowUpIcon(it) },
    icon("arrow-down") { LArrowDownIcon(it) },
    icon("arrow-up-down") { LArrowUpDownIcon(it) },
    icon("circle-alert") { LCircleAlertIcon(it) },
    icon("circle-x") { LCircleXIcon(it) },
    icon("clock") { LClockIcon(it) },
    icon("list-checks") { LListChecksIcon(it) },
    icon("pause") { LPauseIcon(it) },
    icon("trending-up") { LTrendingUpIcon(it) },
    icon("trending-down") { LTrendingDownIcon(it) },
    icon("keyboard") { LKeyboardIcon(it) },
    icon("panel-left") { LPanelLeftIcon(it) },
    icon("lock") { LLockIcon(it) },
    icon("square-check-big") { LSquareCheckIcon(it) },
    icon("square-pen") { LSquarePenIcon(it) },
    icon("git-branch") { LGitBranchIcon(it) },
    icon("bell") { LBellIcon(it) },
    icon("shield") { LShieldIcon(it) },
    icon("credit-card") { LCreditCardIcon(it) },
    icon("receipt") { LReceiptIcon(it) },
    icon("webhook") { LWebhookIcon(it) },
    icon("users") { LUsersIcon(it) },
    icon("user-plus") { LUserPlusIcon(it) },
    icon("scroll-text") { LScrollTextIcon(it) },
    icon("coins") { LCoinsIcon(it) },
    icon("chart-line") { LChartLineIcon(it) },
    icon("tag") { LTagIcon(it) },
    icon("tags") { LTagsIcon(it) },
    icon("archive-restore") { LArchiveRestoreIcon(it) },
)

private fun icon(
    name: String,
    render: @Composable (Modifier) -> Unit,
): Pair<String, @Composable (Modifier) -> Unit> = name to render
